import React, { useState } from 'react';
import { toast } from 'sonner';
import { useT } from '../../i18n/LanguageContext';
import { Link2, ArrowRight, RefreshCw, Trash2, Plus, Loader2, Repeat, Settings2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { schoolApi } from '../../api/school';

export const PairedClassesTools = ({ orgId, academicYearId, yearName, classes = [], onSynced }) => {
  const { t } = useT();

  // config dialog
  const [configOpen, setConfigOpen] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [newSource, setNewSource] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [newAuto, setNewAuto] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // sync dialog
  const [syncOpen, setSyncOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const loadPairs = async () => {
    setPairsLoading(true);
    try {
      setPairs(await schoolApi.listClassPairs(orgId) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.loadFailed', 'Failed to load pairs'));
    } finally { setPairsLoading(false); }
  };

  const openConfig = async () => { setConfigOpen(true); await loadPairs(); };

  const addPair = async () => {
    if (!newSource || !newTarget) { toast.error(t('pairs.pickBoth', 'Pick a source and a target class')); return; }
    if (newSource === newTarget) { toast.error(t('pairs.mustDiffer', 'Source and target must differ')); return; }
    setCreating(true);
    try {
      await schoolApi.createClassPair({ organization_id: orgId, source_class_id: newSource, target_class_id: newTarget, auto_sync: newAuto });
      toast.success(t('pairs.added', 'Pair added'));
      setNewSource(''); setNewTarget(''); setNewAuto(false);
      await loadPairs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.addFailed', 'Failed to add pair'));
    } finally { setCreating(false); }
  };

  const toggleAuto = async (p) => {
    setBusyId(p.id);
    try {
      await schoolApi.updateClassPair(p.id, { auto_sync: !p.auto_sync });
      await loadPairs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.updateFailed', 'Failed to update pair'));
    } finally { setBusyId(null); }
  };

  const reverse = async (p) => {
    setBusyId(p.id);
    try {
      await schoolApi.reverseClassPair(p.id);
      await loadPairs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.reverseFailed', 'Failed to reverse pair'));
    } finally { setBusyId(null); }
  };

  const removePair = async (p) => {
    setBusyId(p.id);
    try {
      await schoolApi.deleteClassPair(p.id);
      await loadPairs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.deleteFailed', 'Failed to delete pair'));
    } finally { setBusyId(null); }
  };

  const openSync = async () => {
    setSyncOpen(true);
    setPreview(null); setResult(null); setConfirmText('');
    setPreviewLoading(true);
    try {
      setPreview(await schoolApi.classPairsSyncPreview(orgId, academicYearId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.previewFailed', 'Failed to load preview'));
      setSyncOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const runSync = async () => {
    setRunning(true);
    try {
      const res = await schoolApi.classPairsSync({ organization_id: orgId, academic_year_id: academicYearId, confirm: confirmText.trim() });
      setResult(res);
      const failed = (res.results || []).filter((r) => r.status === 'failed').length;
      if (failed) toast.warning(t('pairs.syncPartial', `Sync finished with ${failed} failed pair(s)`));
      else toast.success(t('pairs.syncDone', `Synced ${res.pair_count} pair(s): +${res.totals.added} / -${res.totals.removed}`));
      onSynced && onSynced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pairs.syncFailed', 'Sync failed'));
    } finally { setRunning(false); }
  };

  const confirmOk = confirmText.trim().toUpperCase() === 'SYNC';

  return (
    <>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="gap-2" onClick={openConfig} data-testid="configure-pairs-button">
          <Settings2 className="w-4 h-4" /> {t('pairs.configure', 'Configure pairs')}
        </Button>
        <Button size="sm" className="gap-2" onClick={openSync} data-testid="bulk-sync-button">
          <Link2 className="w-4 h-4" /> {t('pairs.bulkSync', 'Bulk Sync Paired Classes')}
        </Button>
      </div>

      {/* Configure paired classes */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-3xl" data-testid="pairs-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> {t('pairs.configTitle', 'Paired Classes')}</DialogTitle>
            <DialogDescription>{t('pairs.configDesc', 'Link a source class to its equivalent class. The source is the source of truth; the target is synced to match it.')}</DialogDescription>
          </DialogHeader>

          {/* Add new pair */}
          <div className="rounded-lg border border-border p-3 bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs">{t('pairs.source', 'Source (source of truth)')}</Label>
                <Select value={newSource} onValueChange={setNewSource}>
                  <SelectTrigger data-testid="pair-source-select"><SelectValue placeholder={t('pairs.selectClass', 'Select class')} /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="pb-2 text-muted-foreground hidden md:block"><ArrowRight className="w-4 h-4" /></div>
              <div className="space-y-1">
                <Label className="text-xs">{t('pairs.target', 'Target (kept in sync)')}</Label>
                <Select value={newTarget} onValueChange={setNewTarget}>
                  <SelectTrigger data-testid="pair-target-select"><SelectValue placeholder={t('pairs.selectClass', 'Select class')} /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={addPair} disabled={creating} className="gap-2" data-testid="pair-add-button">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t('common.add', 'Add')}
              </Button>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer w-fit">
              <Switch checked={newAuto} onCheckedChange={setNewAuto} data-testid="pair-new-autosync" />
              {t('pairs.autoSync', 'Auto-sync future changes')}
            </label>
          </div>

          {/* Existing pairs */}
          <div className="mt-2 max-h-[45vh] overflow-y-auto">
            {pairsLoading ? (
              <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', 'Loading…')}</div>
            ) : pairs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 px-1">{t('pairs.none', 'No pairs configured yet.')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-2 py-2">{t('pairs.direction', 'Direction')}</th>
                  <th className="px-2 py-2 text-center">{t('pairs.autoSyncShort', 'Auto-sync')}</th>
                  <th className="px-2 py-2 text-right">{t('common.actions', 'Actions')}</th>
                </tr></thead>
                <tbody>
                  {pairs.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0" data-testid={`pair-row-${p.id}`}>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{p.source_class_name}</span>
                          <Badge variant="outline" className="text-[10px]">{p.source_program_label}</Badge>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{p.target_class_name}</span>
                          <Badge variant="outline" className="text-[10px]">{p.target_program_label}</Badge>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <Switch checked={!!p.auto_sync} disabled={busyId === p.id} onCheckedChange={() => toggleAuto(p)} data-testid={`pair-autosync-${p.id}`} />
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="gap-1.5" disabled={busyId === p.id} onClick={() => reverse(p)} data-testid={`pair-reverse-${p.id}`}>
                            <Repeat className="w-4 h-4" /> {t('pairs.reverse', 'Reverse')}
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" disabled={busyId === p.id} onClick={() => removePair(p)} data-testid={`pair-delete-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)} data-testid="pairs-config-close">{t('common.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk sync preview + confirm + result */}
      <Dialog open={syncOpen} onOpenChange={(o) => { if (!o && !running) setSyncOpen(false); }}>
        <DialogContent className="max-w-3xl" data-testid="pairs-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> {t('pairs.bulkSync', 'Bulk Sync Paired Classes')}</DialogTitle>
            <DialogDescription>
              {result ? t('pairs.resultDesc', 'Sync complete. Results per pair below.')
                : t('pairs.previewDesc', 'Review the changes for every pair, then type SYNC to run. The target class will be made to match the source exactly.')}
              {preview?.academic_year_name ? ` · ${preview.academic_year_name}` : (yearName ? ` · ${yearName}` : '')}
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', 'Loading…')}</div>
          ) : result ? (
            /* ---- RESULT ---- */
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {t('pairs.resultTotals', 'Totals')}: <span className="text-foreground font-medium">+{result.totals.added}</span> {t('pairs.added', 'added')} · <span className="text-foreground font-medium">-{result.totals.removed}</span> {t('pairs.removed', 'removed')} · <span className="text-foreground font-medium">{result.totals.moved}</span> {t('pairs.moved', 'moved')}
              </div>
              <div className="max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-2 py-2">{t('pairs.pair', 'Pair')}</th>
                    <th className="px-2 py-2 text-right">+{t('pairs.add', 'add')}</th>
                    <th className="px-2 py-2 text-right">-{t('pairs.remove', 'remove')}</th>
                    <th className="px-2 py-2 text-right">{t('pairs.move', 'move')}</th>
                    <th className="px-2 py-2 text-center">{t('common.status', 'Status')}</th>
                  </tr></thead>
                  <tbody>
                    {result.results.map((r) => (
                      <tr key={r.pair_id} className="border-b border-border last:border-0" data-testid={`sync-result-${r.pair_id}`}>
                        <td className="px-2 py-2 font-medium">{r.direction_label}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-emerald-600">{r.added}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-destructive">{r.removed}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{r.moved}</td>
                        <td className="px-2 py-2 text-center">
                          {r.status === 'success'
                            ? <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="w-3 h-3" /> {t('pairs.success', 'Success')}</Badge>
                            : <Badge variant="destructive" className="gap-1" title={r.error || ''}><XCircle className="w-3 h-3" /> {t('pairs.failed', 'Failed')}</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : preview ? (
            /* ---- PREVIEW ---- */
            <div className="space-y-3">
              {preview.pair_count === 0 ? (
                <div className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm text-muted-foreground">
                  <AlertTriangle className="w-4 h-4 mt-0.5" /> {t('pairs.noneConfigured', 'No pairs configured. Add pairs first via “Configure pairs”.')}
                </div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    {t('pairs.previewTotals', 'Across all pairs')}: <span className="text-emerald-600 font-medium">+{preview.totals.add}</span> · <span className="text-destructive font-medium">-{preview.totals.remove}</span> · {preview.totals.move} {t('pairs.moved', 'moved')}
                  </div>
                  <div className="max-h-[45vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="px-2 py-2">{t('pairs.pair', 'Pair')}</th>
                        <th className="px-2 py-2 text-right">+{t('pairs.add', 'add')}</th>
                        <th className="px-2 py-2 text-right">-{t('pairs.remove', 'remove')}</th>
                        <th className="px-2 py-2 text-right">{t('pairs.move', 'move')}</th>
                        <th className="px-2 py-2">{t('pairs.sections', 'Sections')}</th>
                      </tr></thead>
                      <tbody>
                        {preview.pairs.map((p) => (
                          <tr key={p.pair_id} className="border-b border-border last:border-0" data-testid={`sync-preview-${p.pair_id}`}>
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1.5 font-medium">{p.direction_label}{p.auto_sync ? <Badge variant="outline" className="text-[10px]">auto</Badge> : null}</div>
                              {p.error ? <div className="text-xs text-destructive mt-0.5">{p.error}</div> : null}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-emerald-600">{p.add_count}</td>
                            <td className="px-2 py-2 text-right tabular-nums text-destructive">{p.remove_count}</td>
                            <td className="px-2 py-2 text-right tabular-nums">{p.move_count}</td>
                            <td className="px-2 py-2 text-muted-foreground text-xs">{(p.sections_affected || []).join(', ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-2 pt-1">
                    <Label htmlFor="sync-confirm-input">{t('pairs.typeSync', 'Type SYNC to confirm')}</Label>
                    <Input id="sync-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SYNC" autoComplete="off" data-testid="sync-confirm-input" />
                  </div>
                </>
              )}
            </div>
          ) : null}

          <DialogFooter>
            {result ? (
              <Button onClick={() => setSyncOpen(false)} data-testid="sync-result-close">{t('common.close', 'Close')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={running} data-testid="sync-cancel">{t('common.cancel', 'Cancel')}</Button>
                <Button onClick={runSync} disabled={running || previewLoading || !preview || preview.pair_count === 0 || !confirmOk} className="gap-2" data-testid="sync-run-button">
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {running ? t('pairs.syncing', 'Syncing…') : t('pairs.runSync', 'Run sync')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PairedClassesTools;
